// @ts-strict-ignore
import {AfterViewInit, Component, ElementRef, Injector, ViewChild} from "@angular/core";
import {
	BasicMaterialDialog,
	MatDialogDataBaseInput,
	MatDialogDataBaseOutput,
} from "../../basic-material-dialog/basic-material-dialog.directive";
import {zoomInOnEnterAnimation, zoomOutOnLeaveAnimation} from "angular-animations";
import {animate, query, sequence, stagger, style, transition, trigger} from "@angular/animations";
import {TranslateService} from "@ngx-translate/core";
import {HttpEventType} from "@angular/common/http";
import {NotificationService, SimConfigurationService, SimEventService, SimUploadService, SimUtilityService} from "@services";
import {Event as EventInfo, EventAttachment, MediaType, UploadAttachmentErrorCode} from "@models";
import {Translations} from "@translations";
import {DomSanitizer} from "@angular/platform-browser";

export interface DialogData_UploadAttachmentDialogInput extends MatDialogDataBaseInput {
	eventInfo?: EventInfo;
}

export interface DialogData_UploadAttachmentDialogOutput extends MatDialogDataBaseOutput {
	uploadedFiles?: boolean;
}

@Component({
	selector: "sim-upload-attachment-dialog",
	templateUrl: "./upload-attachment-dialog.component.html",
	styleUrls: ["./upload-attachment-dialog.component.scss"],
	animations: [
		zoomInOnEnterAnimation({duration: 175, delay: 0}),
		zoomOutOnLeaveAnimation({duration: 175, delay: 0}),
		trigger("listStagger", [
			transition("* <=> *", [
				query(
					":enter",
					[
						style({opacity: 0, transform: "translateY(-15px)"}),
						stagger(
							"50ms",
							animate(
								"550ms ease-out",
								style({
									opacity: 1,
									transform: "translateY(0px)",
								}),
							),
						),
					],
					{optional: true},
				),
				query(":leave", animate("200ms", style({opacity: 0})), {
					optional: true,
				}),
			]),
		]),
		trigger("anim", [
			transition("* => void", [
				style({
					height: "*",
					opacity: "1",
					transform: "translateX(0)",
					"box-shadow": "none",
				}),
				sequence([
					animate(
						".25s ease",
						style({
							height: "*",
							opacity: ".2",
							transform: "translateX(30px)",
							"box-shadow": "none",
						}),
					),
					animate(
						".1s ease",
						style({
							height: "0",
							opacity: 0,
							transform: "translateX(30px)",
							"box-shadow": "none",
						}),
					),
				]),
			]),
			transition("void => active", [
				style({
					height: "*",
					opacity: "0",
					transform: "translateX(30px)",
					"box-shadow": "none",
				}),
				sequence([
					animate(
						".1s ease",
						style({
							height: "*",
							opacity: ".2",
							transform: "translateX(30px)",
							"box-shadow": "none",
						}),
					),
					animate(
						".25s ease",
						style({
							height: "*",
							opacity: 1,
							transform: "translateX(0)",
							"box-shadow": "none",
						}),
					),
				]),
			]),
		]),
	],
	standalone: false,
})
export class UploadAttachmentDialogComponent
	extends BasicMaterialDialog<
		UploadAttachmentDialogComponent,
		DialogData_UploadAttachmentDialogInput,
		DialogData_UploadAttachmentDialogOutput
	>
	implements AfterViewInit
{
	T = Translations;

	MediaType = MediaType;
	SimUtilityService = SimUtilityService;
	afterViewInit: boolean;

	@ViewChild("uploadInput", {static: false}) uploadInput: ElementRef;
	currentSelectedFiles: (File & {
		mediaUrl?: string;
		newName?: string;
		progress?: number;
		isPlaying?: boolean;
		progressUploading?: number;
		isUploading?: boolean;
		errorMessage?: string;
	})[];
	acceptedFileTypes: string = "*";
	public currentFileIndex: number = -1;
	public uploadingMediaFiles: boolean;
	public uploadedFiles: boolean = false;

	constructor(
		private _simNotificationService: NotificationService,
		private _simUploadService: SimUploadService,
		private _simEventService: SimEventService,
		private domSanitizer: DomSanitizer,
		private _translate: TranslateService,
		_injector: Injector,
	) {
		super(_injector);
	}

	ngAfterViewInit() {
		this.afterViewInit = true;
	}

	chooseFiles() {
		if (!this.currentSelectedFiles) {
			this.uploadInput.nativeElement.click();
		} else {
			if (this.currentFileIndex != -1) {
				this.currentSelectedFiles[this.currentFileIndex].isPlaying = false;
				this.currentSelectedFiles[this.currentFileIndex].progress = 0;
			}
			this.currentSelectedFiles = undefined;
			this.uploadInput.nativeElement.value = "";
			this.uploadInput.nativeElement.click();
		}
	}

	handleFileInput(event: Event) {
		const files = (event.target as HTMLInputElement).files;

		if (files?.length === 0) {
			this._simNotificationService.notifyInfo(this._translate.instant(this.T.EVENT_CREATION_ATTACHMENT_DIALOG_NOTIFYERROR));
			this.currentSelectedFiles = undefined;
			return;
		}

		this.currentSelectedFiles = Array.from(files);
		this.currentSelectedFiles.forEach((value) => {
			value.mediaUrl = <any>this.domSanitizer.bypassSecurityTrustResourceUrl(URL.createObjectURL(value));
			/*
					if (this.data?.type === MediaType.Video) {
						value.mediaUrl = this.domSanitizer.bypassSecurityTrustResourceUrl(URL.createObjectURL(value));
					} else {
						value.mediaUrl = URL.createObjectURL(value);
					}
				*/
		});
	}

	deleteFromSelection(i: number) {
		this.currentSelectedFiles.splice(i, 1);
	}

	uploadFiles() {
		this.uploadedFiles = true;

		this.uploadingMediaFiles = true;

		let failedCount = 0;
		let successfulCount = 0;
		let totalCount = this.currentSelectedFiles.length;
		let showedResultDialog = false;

		let resultingAttachments: EventAttachment[] = [];

		this.currentSelectedFiles.forEach((file) => {
			file.progressUploading = 0;
			file.isUploading = true;

			this._simUploadService
				.uploadAttachment({
					uploadedFile: file,
					eventId: this.data.eventInfo.id,
					description: file.newName || file.name,
				})
				.subscribe({
					next: (uploadData) => {
						if (uploadData.type === HttpEventType.UploadProgress) {
							file.progressUploading = Math.round((100 * uploadData.loaded) / uploadData.total);
						} else if (uploadData.type === HttpEventType.Response) {
							let response = uploadData.body;
							if (!response.success) {
								failedCount++;

								let errorMessage = this._translate.instant(this.T.EVENT_CREATION_ATTACHMENT_DIALOG_NOTIFYERROR_UNKNOWN);
								switch (response.errorCode) {
									case UploadAttachmentErrorCode.DuplicateFileName:
										errorMessage = this._translate.instant(
											this.T.EVENT_CREATION_ATTACHMENT_DIALOG_NOTIFYERROR_DUPLICATE,
										);
										break;
									case UploadAttachmentErrorCode.FileExtensionBanned:
										errorMessage = this._translate.instant(
											this.T.EVENT_CREATION_ATTACHMENT_DIALOG_NOTIFYERROR_EXTENSION_BANNED,
										);
										break;
									case UploadAttachmentErrorCode.FileNullOrEmpty:
										errorMessage = this._translate.instant(
											this.T.EVENT_CREATION_ATTACHMENT_DIALOG_NOTIFYERROR_FILE_EMPTY,
										);
										break;
									case UploadAttachmentErrorCode.FileTooLarge:
										errorMessage = this._translate.instant(
											this.T.EVENT_CREATION_ATTACHMENT_DIALOG_NOTIFYERROR_TOO_LARGE,
											{
												fileSizeLimit: SimConfigurationService.settings.attachmentMaxSize,
											},
										);
										break;
								}

								file.errorMessage = errorMessage;
							} else {
								successfulCount++;
								resultingAttachments.push(response.createdAttachment);
							}
							file.isUploading = false;

							setTimeout(() => {
								if (file.progressUploading === 100) {
									if (!file.errorMessage) {
										this.currentSelectedFiles.forEach((item, index, array) => {
											if (item === file) {
												array.splice(index, 1);
											}
										});
									}
								}
							}, 200);
						}
					},
					error: (err) => {
						if (err.status === 413) {
							failedCount++;
							file.isUploading = false;
							file.errorMessage = this._translate.instant(
								this.T.EVENT_CREATION_ATTACHMENT_DIALOG_NOTIFYERROR_TOO_LARGE_FOR_SERVER,
								{fileName: file.name},
							);
							this._simNotificationService.notifyError(file.errorMessage);
						}
						console.log(err);
					},
					complete: () => {
						if (!showedResultDialog && failedCount + successfulCount >= totalCount) {
							showedResultDialog = true;

							let encounteredError = failedCount > 0;

							if (successfulCount > 0) {
								this._simEventService
									.addAttachments({
										eventId: this.data.eventInfo.id,
										attachments: resultingAttachments,
									})
									.then((result) => {
										if (result.success && !encounteredError) {
											this._simNotificationService.notifySuccess(
												this._translate.instant(this.T.EVENT_CREATION_ATTACHMENTS_ADDED),
											);
										} else {
											this._simNotificationService.notifyError(
												this._translate.instant(this.T.EVENT_CREATION_ATTACHMENTS_ISSUES),
											);
										}
									});
							} else {
								this._simNotificationService.notifyError(this._translate.instant(this.T.EVENT_CREATION_ATTACHMENTS_ISSUES));
							}
						}
					},
				});
		});
	}
}
